import { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

/* COMPONENTS */
import Footer from "layout/footer";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Card, CardBody } from "@material-tailwind/react";

/* ICONS */
import { IoEye, IoEyeOff } from "react-icons/io5";

/* APIS */
import actions from "../../redux/actions/userAction";
import { adminRegister } from "service/api";

/* VALIDATION SCHEMA */
const validationSchema = yup.object().shape({
  firstName: yup.string().required("First name is required."),
  lastName: yup.string().required("Last name is required."),
  email: yup.string().email().required("Email is required."),
  password: yup.string().required("Password is required."),
});

const Register = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [togglePassword, setTogglePassword] = useState(false);

  /* FORM HANDLER */
  const formHandler = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setLoading(true);
      adminRegister(values)
        .then((res) => {
          if (res?.status) {
            toast.success(res?.message);
            dispatch(actions.setToken(res?.data?.token));
            dispatch(actions.setUser(res?.data?.userData));
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          setLoading(false);
          toast.error(e?.response?.data?.message);
        })
        .finally((res) => {
          setLoading(false);
        });
    },
  });

  return (
    <section className="p-0">
      <div className="container">
        {/* TOP SECTION */}
        <div className="h-[93vh] flex items-center justify-center">
          <Card className="w-full xl:w-[60vh] lg:w-[60vh] bg-white border border-greyBorder rounded-xl">
            <CardBody className="p-5 xl:p-8 lg:p-8">
              <img
                src="https://storage.googleapis.com/cdn-roundtechsquare/nick-itkin/logos/nick-itkin-foundation-dark.png"
                className="object-contain w-[15vh] h-20"
                alt="The Nick Itkin Foundation"
              />

              <div className="mt-5">
                <h5 className="text-lg font-source font-bold text-black">
                  Admin Dashboard
                </h5>
                <p className="text-sm font-source font-normal text-greyText">
                  Create your account to manage the contact us queries from your
                  website.
                </p>
              </div>

              <form className="mt-5" onSubmit={formHandler.handleSubmit}>
                <div className="flex items-center gap-5">
                  <div className="p-0">
                    <label className="text-sm font-source font-normal text-greyText">
                      First name
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="firstName"
                        placeholder="First name"
                        value={formHandler.values.firstName}
                        onChange={formHandler.handleChange}
                        className={`w-full block outline-none focus:outline-none focus:border-black text-sm font-source font-medium text-black px-5 py-2.5 rounded-md border ${
                          formHandler.errors.firstName &&
                          formHandler.touched.firstName
                            ? "border-red-400"
                            : "border-greyBorder"
                        }`}
                      />
                    </div>
                  </div>
                  <div className="p-0">
                    <label className="text-sm font-source font-normal text-greyText">
                      Last name
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Last name"
                        value={formHandler.values.lastName}
                        onChange={formHandler.handleChange}
                        className={`w-full block outline-none focus:outline-none focus:border-black text-sm font-source font-medium text-black px-5 py-2.5 rounded-md border ${
                          formHandler.errors.lastName &&
                          formHandler.touched.lastName
                            ? "border-red-400"
                            : "border-greyBorder"
                        }`}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5">
                  <label className="text-sm font-source font-normal text-greyText">
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter email address"
                      value={formHandler.values.email}
                      onChange={formHandler.handleChange}
                      className={`w-full block outline-none focus:outline-none focus:border-black text-sm font-source font-medium text-black px-5 py-2.5 rounded-md border ${
                        formHandler.errors.email && formHandler.touched.email
                          ? "border-red-400"
                          : "border-greyBorder"
                      }`}
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <label className="text-sm font-source font-normal text-greyText">
                    Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      type={togglePassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter password"
                      value={formHandler.values.password}
                      onChange={formHandler.handleChange}
                      className={`w-full block outline-none focus:outline-none focus:border-black text-sm font-source font-medium text-black px-5 py-2.5 rounded-md border ${
                        formHandler.errors.password &&
                        formHandler.touched.password
                          ? "border-red-400"
                          : "border-greyBorder"
                      }`}
                    />

                    <div className="absolute top-1.5 right-1.5">
                      <Button
                        onClick={() => setTogglePassword(!togglePassword)}
                        className="p-2 shadow-none hover:shadow-none rounded-full hover:bg-black hover:bg-opacity-10 transition-all duration-300 ease-in-out bg-transparent"
                      >
                        {togglePassword ? (
                          <IoEyeOff className="w-4 h-4 text-greyText" />
                        ) : (
                          <IoEye className="w-4 h-4 text-greyText" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="mt-5 w-full shadow-none hover:shadow-none bg-primary hover:bg-opacity-70 text-sm font-source font-medium text-white transition-all duration-300 ease-in-out normal-case"
                >
                  {loading ? "Please wait..." : "Create your account"}
                </Button>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* FOOTER */}
      <Footer />
    </section>
  );
};

export default Register;
